import { GetClientFlags200Response } from "@coeff/api";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { AppThunkDispatch, getClientFlags } from "../actions";
import { QueryCacheKey } from "../api/queryClient";

const CLIENT_FLAGS_REFETCH_INTERVAL_MS = 1000 * 60 * 5;

// Errored with no data ever: the unauthenticated case, where getClientFlags always 401s.
// Refetching would just repeat the 401 (and flip the query back to `loading` in rq v4).
const hasOnlyErrored = (query: { state: { dataUpdatedAt: number; errorUpdateCount: number } }) =>
  query.state.dataUpdatedAt === 0 && query.state.errorUpdateCount > 0;

/**
 * Fetches client flags and keeps Redux in sync on every refetch. Overrides the
 * global queryClient defaults so flags refresh every 5 minutes and on refocus —
 * except for unauthenticated viewers, where refetching is pointless.
 */
export const useClientFlagsQuery = () => {
  const dispatch = useDispatch<AppThunkDispatch>();

  return useQuery(
    [QueryCacheKey.CLIENT_FLAGS],
    async () => (await dispatch(getClientFlags())) as unknown as GetClientFlags200Response,
    {
      refetchOnWindowFocus: query => !hasOnlyErrored(query),
      refetchInterval: (_data, query) =>
        hasOnlyErrored(query) ? false : CLIENT_FLAGS_REFETCH_INTERVAL_MS,
      // staleTime 0 so the focus refetch isn't skipped as still-fresh (rq v4).
      staleTime: 0,
      retry: false,
    }
  );
};
